/**
 * Error Log page - displays persistent error/warning history
 * @module ErrorLogPage
 */

import { ArrowLeft, Trash2, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FrogMascot } from '@/components/FrogMascot';
import { PageHeader } from '@/components/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useErrorLogStore } from '@/stores/errorLogStore';
import { FRIENDLY_SOURCE_NAMES } from '@/types/errorLog';
import type { ErrorSeverity, ErrorLogEntry } from '@/types/errorLog';

type SeverityFilter = ErrorSeverity | 'all';

/**
 * Format timestamp for display
 */
function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

/**
 * Get badge variant for severity
 */
function getSeverityVariant(severity: ErrorSeverity): 'destructive' | 'secondary' | 'outline' {
  switch (severity) {
    case 'error':
      return 'destructive';
    case 'warning':
      return 'secondary';
    default:
      return 'outline';
  }
}

/**
 * Individual error log entry component
 */
function ErrorLogEntryItem({ entry }: { entry: ErrorLogEntry }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDetails = entry.technicalDetails || entry.originalError || entry.context;

  return (
    <div
      className={cn(
        'border rounded-md p-3 transition-colors',
        entry.severity === 'error' && 'border-destructive/30 bg-destructive/5',
        entry.severity === 'warning' && 'border-amber-500/30 bg-amber-500/5',
        entry.severity === 'info' && 'border-blue-500/30 bg-blue-500/5'
      )}
    >
      <div className="flex items-start gap-2">
        {hasDetails && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-0.5 p-0.5 hover:bg-muted rounded"
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
        {!hasDetails && <div className="w-5" />}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={getSeverityVariant(entry.severity)} className="text-xs">
              {entry.severity}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {FRIENDLY_SOURCE_NAMES[entry.source]}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {formatTimestamp(entry.timestamp)}
            </span>
          </div>
          <p className="mt-1 text-sm">{entry.userMessage}</p>

          {isExpanded && hasDetails && (
            <div className="mt-2 space-y-2 text-xs text-muted-foreground">
              {entry.technicalDetails && (
                <p>
                  <span className="font-medium">Details:</span> {entry.technicalDetails}
                </p>
              )}
              {entry.originalError && (
                <p>
                  <span className="font-medium">Error:</span> {entry.originalError}
                </p>
              )}
              {entry.context && (
                <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-x-auto">
                  {JSON.stringify(entry.context, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Error Log page component
 */
export function ErrorLogPage() {
  const navigate = useNavigate();
  const { entries, clearAll } = useErrorLogStore();
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Filter entries
  const filteredEntries = useMemo(() => {
    if (severityFilter === 'all') return entries;
    return entries.filter((e) => e.severity === severityFilter);
  }, [entries, severityFilter]);

  // Copy log to clipboard
  const handleCopyLog = useCallback(async () => {
    const logText = filteredEntries
      .map((e) => {
        const time = formatTimestamp(e.timestamp);
        const details = e.technicalDetails ? ` | ${e.technicalDetails}` : '';
        const error = e.originalError ? ` | Error: ${e.originalError}` : '';
        return `[${time}] [${e.severity.toUpperCase()}] [${e.source}] ${e.userMessage}${details}${error}`;
      })
      .join('\n');

    try {
      await navigator.clipboard.writeText(logText);
      // Could show a toast here, but avoiding circular dependency
    } catch {
      console.error('[ERROR_LOG_PAGE] Failed to copy to clipboard');
    }
  }, [filteredEntries]);

  // Handle clear with confirmation
  const handleClear = useCallback(() => {
    if (showClearConfirm) {
      clearAll();
      setShowClearConfirm(false);
    } else {
      setShowClearConfirm(true);
      // Auto-reset after 3 seconds
      setTimeout(() => setShowClearConfirm(false), 3000);
    }
  }, [showClearConfirm, clearAll]);

  return (
    <div className="flex h-full flex-col p-4 overflow-y-auto">
      <div className="w-full max-w-card mx-auto">
        {/* Back navigation */}
        <div className="pt-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>
        <PageHeader variant="error" title="Error Log" />

        {/* Controls */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex flex-wrap gap-2 items-center justify-between">
              {/* Severity filter */}
              <div className="flex gap-1">
                {(['all', 'error', 'warning', 'info'] as const).map((sev) => (
                  <Button
                    key={sev}
                    variant={severityFilter === sev ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSeverityFilter(sev)}
                    className="text-xs"
                  >
                    {sev === 'all' ? 'All' : sev.charAt(0).toUpperCase() + sev.slice(1)}
                  </Button>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLog}
                  disabled={filteredEntries.length === 0}
                >
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
                <Button
                  variant={showClearConfirm ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={handleClear}
                  disabled={entries.length === 0}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  {showClearConfirm ? 'Confirm Clear' : 'Clear All'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Entry count */}
        <p className="text-sm text-muted-foreground mb-2">
          {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'}
          {severityFilter !== 'all' && ` (${severityFilter})`}
        </p>

        {/* Entries list */}
        {filteredEntries.length === 0 ? (
          <Card className="card-elevated">
            <CardContent className="py-12 text-center">
              <div className="flex justify-center mb-4">
                <FrogMascot variant="error" size="md" />
              </div>
              <p className="text-lg font-medium text-muted-foreground">
                Nothing to report. All clear.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                No errors or warnings have been recorded.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredEntries.map((entry) => (
              <ErrorLogEntryItem key={entry.id} entry={entry} />
            ))}
          </div>
        )}

        <footer className="py-4 text-center text-xs text-muted-foreground">
          <div className="h-px w-24 mx-auto mb-4 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          <p>Entries are stored locally (max 100)</p>
        </footer>
      </div>
    </div>
  );
}
